import {IPublicClientApplication} from '@azure/msal-browser';
import {UserRole} from 'types';

/** X-Active-Role header name. MUST match the backend's ActiveRoleClaimsTransformation. */
export const ACTIVE_ROLE_HEADER = 'X-Active-Role';

const STORAGE_PREFIX = 'trace-active-role:';
const ROLE_SEPARATOR = ',';

const storageKey = (userId: string) => `${STORAGE_PREFIX}${userId}`;

// Distinct, trimmed, non-empty. Casing kept verbatim so it matches the backend token claims.
const toDistinctRoles = (values: string[]): UserRole[] => {
	const seen = new Set<string>();
	const roles: UserRole[] = [];
	for (const value of values) {
		const role = value.trim();
		if (role && !seen.has(role)) {
			seen.add(role);
			roles.push(role as UserRole);
		}
	}

	return roles;
};

/** Stored active-role subset for a user; empty means "no narrowing" (act as the full union). */
export const readStoredActiveRoles = (userId: string): UserRole[] => {
	if (!userId) {
		return [];
	}

	const raw = window.localStorage.getItem(storageKey(userId));
	return raw ? toDistinctRoles(raw.split(ROLE_SEPARATOR)) : [];
};

/** Persists the subset; an empty subset clears storage (restores the union). */
export const writeStoredActiveRoles = (
	userId: string,
	roles: UserRole[],
): void => {
	if (!userId) {
		return;
	}

	const distinct = toDistinctRoles(roles);
	if (distinct.length > 0) {
		window.localStorage.setItem(
			storageKey(userId),
			distinct.join(ROLE_SEPARATOR),
		);
	} else {
		window.localStorage.removeItem(storageKey(userId));
	}
};

/** Stored roles still held by the user (empty => full union). Pure; see pruneStaleActiveRoles. */
export const resolveActiveRoles = (
	userId: string,
	eligibleRoles: UserRole[],
): UserRole[] => {
	const stored = readStoredActiveRoles(userId);
	return stored.filter(role => eligibleRoles.includes(role));
};

/** Drops stored roles the user no longer holds, so a later re-grant can't re-activate a stale pick. */
export const pruneStaleActiveRoles = (
	userId: string,
	eligibleRoles: UserRole[],
): void => {
	const stored = readStoredActiveRoles(userId);
	if (stored.length === 0) {
		return;
	}

	const live = stored.filter(role => eligibleRoles.includes(role));
	if (live.length !== stored.length) {
		writeStoredActiveRoles(userId, live);
	}
};

/** Comma-joined subset for the active account's X-Active-Role header ('' = full union). */
export const getActiveRoleHeaderValue = (
	msalInstance: IPublicClientApplication,
): string => {
	const userId = msalInstance.getActiveAccount()?.localAccountId ?? '';
	return readStoredActiveRoles(userId).join(ROLE_SEPARATOR);
};

/** X-Active-Role header object for a subset, or {} when none is active. */
export const activeRoleHeader = (
	activeRoles: string,
): Record<string, string> =>
	activeRoles ? {[ACTIVE_ROLE_HEADER]: activeRoles} : {};
